import type { StoreBackend } from './storeBackends'
import { sdkConsole } from './logger'

export interface TransferOptions {
    batchWindowMs?: number
    highPriorityBatchWindowMs?: number
    highPriorityMaxBatchWindowMs?: number
    burstIdleResetMs?: number
    maxBatchSize?: number
    maxConcurrency?: number
}

interface QueuedRequest {
    resolve: (data: Uint8Array | null) => void
    reject: (err: unknown) => void
}

let transferBatchSeq = 0

export class TransferOrchestrator {
    // Requests waiting to be batched (debounce window)
    private pending = new Map<string, QueuedRequest[]>()

    // Requests currently in the batch queue or executing
    private inflight = new Map<string, QueuedRequest[]>()

    private activeRequests = 0
    private flushTimer: ReturnType<typeof setTimeout> | null = null
    private flushTimerDelayMs: number | null = null

    private readonly batchWindowMs: number
    private readonly highPriorityBatchWindowMinMs: number
    private readonly highPriorityBatchWindowMaxMs: number
    private readonly burstIdleResetMs: number
    private readonly maxBatchSize: number
    private readonly maxConcurrency: number
    private adaptiveHighPriorityBatchWindowMs: number
    private singletonFlushStreak = 0
    private lastFlushAtMs = 0

    private batchQueue: string[][] = []

    constructor(
        private backend: StoreBackend,
        opts: TransferOptions = {}
    ) {
        this.batchWindowMs = opts.batchWindowMs ?? 20
        const highPriorityMin = Math.max(0, Math.trunc(opts.highPriorityBatchWindowMs ?? 4))
        const highPriorityMax = Math.max(highPriorityMin, Math.trunc(opts.highPriorityMaxBatchWindowMs ?? 32))
        this.highPriorityBatchWindowMinMs = highPriorityMin
        this.highPriorityBatchWindowMaxMs = highPriorityMax
        this.burstIdleResetMs = Math.max(1, Math.trunc(opts.burstIdleResetMs ?? 250))
        this.adaptiveHighPriorityBatchWindowMs = highPriorityMin
        this.maxBatchSize = opts.maxBatchSize ?? 100
        this.maxConcurrency = opts.maxConcurrency ?? 5
    }

    load(hash: string, _priority: 'high' | 'low' = 'high'): Promise<Uint8Array | null> {
        return new Promise((resolve, reject) => {
            // Check if already inflight
            const inflightReqs = this.inflight.get(hash)
            if (inflightReqs) {
                sdkConsole.trace('[sqlite3mt:orchestrator] join-inflight', {
                    hash,
                    listeners: inflightReqs.length + 1,
                })
                inflightReqs.push({ resolve, reject })
                return
            }

            // Check if already pending (waiting for flush)
            const pendingReqs = this.pending.get(hash)
            if (pendingReqs) {
                sdkConsole.trace('[sqlite3mt:orchestrator] join-pending', {
                    hash,
                    listeners: pendingReqs.length + 1,
                })
                pendingReqs.push({ resolve, reject })
                return
            }

            // New request
            this.pending.set(hash, [{ resolve, reject }])
            sdkConsole.trace('[sqlite3mt:orchestrator] enqueue', { hash, priority: _priority })
            this.scheduleFlush(_priority === 'high' ? this.adaptiveHighPriorityBatchWindowMs : this.batchWindowMs)
        })
    }

    loadBatch(hashes: string[]): Promise<Map<string, Uint8Array>> {
        // Helper to load multiple, returning a map
        return Promise.all(hashes.map(h => this.load(h).then(d => d ? [h, d] as const : null)))
            .then(pairs => new Map(pairs.filter((p): p is [string, Uint8Array] => p !== null)))
    }

    private scheduleFlush(delayMs: number) {
        if (this.flushTimer && this.flushTimerDelayMs != null && this.flushTimerDelayMs <= delayMs) {
            return
        }
        if (this.flushTimer) {
            clearTimeout(this.flushTimer)
        }
        this.flushTimerDelayMs = delayMs
        this.flushTimer = setTimeout(() => this.flush(), delayMs)
    }

    private flush() {
        this.flushTimer = null
        this.flushTimerDelayMs = null
        if (this.pending.size === 0) return

        const hashes = Array.from(this.pending.keys())
        this.observeFlush(hashes.length)
        sdkConsole.trace('[sqlite3mt:orchestrator] flush', {
            pending: hashes,
            activeRequests: this.activeRequests,
            adaptiveHighPriorityBatchWindowMs: this.adaptiveHighPriorityBatchWindowMs,
        })

        // Move all pending to inflight
        for (const hash of hashes) {
            const reqs = this.pending.get(hash)!
            this.pending.delete(hash)
            this.inflight.set(hash, reqs)
        }

        // Split into batches
        for (let i = 0; i < hashes.length; i += this.maxBatchSize) {
            const batch = hashes.slice(i, i + this.maxBatchSize)
            this.batchQueue.push(batch)
        }

        this.processQueue()
    }

    private observeFlush(requestCount: number) {
        const now = Date.now()
        const idleMs = this.lastFlushAtMs > 0 ? now - this.lastFlushAtMs : Number.POSITIVE_INFINITY
        this.lastFlushAtMs = now

        if (idleMs > this.burstIdleResetMs) {
            this.singletonFlushStreak = 0
            this.adaptiveHighPriorityBatchWindowMs = this.highPriorityBatchWindowMinMs
            return
        }

        if (requestCount <= 1) {
            this.singletonFlushStreak += 1
            if (this.singletonFlushStreak >= 2) {
                this.adaptiveHighPriorityBatchWindowMs = Math.min(
                    this.highPriorityBatchWindowMaxMs,
                    Math.max(this.highPriorityBatchWindowMinMs, this.adaptiveHighPriorityBatchWindowMs * 2 || 1),
                )
            }
            return
        }

        this.singletonFlushStreak = 0
        this.adaptiveHighPriorityBatchWindowMs = Math.max(
            this.highPriorityBatchWindowMinMs,
            Math.floor(this.adaptiveHighPriorityBatchWindowMs / 2),
        )
    }

    private processQueue() {
        while (this.activeRequests < this.maxConcurrency && this.batchQueue.length > 0) {
            const batch = this.batchQueue.shift()
            if (batch) {
                this.activeRequests++
                const batchId = ++transferBatchSeq
                sdkConsole.trace('[sqlite3mt:orchestrator] batch:start', {
                    batchId,
                    hashes: batch,
                    activeRequests: this.activeRequests,
                })
                // Determine priority? For now simple FIFO.
                this.executeBatch(batch, batchId).finally(() => {
                    this.activeRequests--
                    sdkConsole.trace('[sqlite3mt:orchestrator] batch:finish', {
                        batchId,
                        activeRequests: this.activeRequests,
                    })
                    this.processQueue()
                })
            }
        }
    }

    private async executeBatch(hashes: string[], batchId: number) {
        try {
            // Filter out any hashes that might have been cancelled or satisfied externally?
            // For now assume all valid.
            const results = await this.backend.loadBatch(hashes)
            sdkConsole.trace('[sqlite3mt:orchestrator] batch:loaded', {
                batchId,
                hashes,
                found: Array.from(results.keys()),
            })

            for (const hash of hashes) {
                const data = results.get(hash) ?? null
                this.resolveInflight(hash, data)
            }
        } catch (error) {
            sdkConsole.warn('[sqlite3mt:orchestrator] batch:error', {
                batchId,
                hashes,
                error,
            })
            // On batch failure, fail all items in this batch
            for (const hash of hashes) {
                this.rejectInflight(hash, error)
            }
        }
    }

    private resolveInflight(hash: string, data: Uint8Array | null) {
        const waiting = this.inflight.get(hash)
        if (!waiting) return
        this.inflight.delete(hash)
        for (const w of waiting) w.resolve(data)
    }

    private rejectInflight(hash: string, error: unknown) {
        const waiting = this.inflight.get(hash)
        if (!waiting) return
        this.inflight.delete(hash)
        for (const w of waiting) w.reject(error)
    }
}
